import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Trash2, Loader2, AlertTriangle, Tag
} from 'lucide-react';

const ProblemTypes = () => {
  const [problemTypes, setProblemTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newType, setNewType] = useState('');
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState({});
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchProblemTypes();
  }, []);

  const fetchProblemTypes = async () => {
    try {
      const response = await api.getProblemTypes();
      setProblemTypes(response.data);
    } catch (error) {
      toast.error('Failed to fetch problem types');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newType.trim()) {
      toast.error('Please enter a problem type name');
      return;
    }

    setAdding(true);
    try {
      await api.createProblemType(newType.trim());
      toast.success('Problem type added');
      setNewType('');
      fetchProblemTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add problem type');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;

    setDeleting(prev => ({ ...prev, [id]: true }));
    try {
      await api.deleteProblemType(id);
      toast.success('Problem type deleted');
      fetchProblemTypes();
    } catch (error) {
      toast.error('Failed to delete problem type');
    } finally {
      setDeleting(prev => ({ ...prev, [id]: false }));
    }
  };

  return (
    <div data-testid="problem-types-page" className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/complaints/dashboard')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold font-headings text-slate-900 flex items-center gap-3">
          <Tag size={28} />
          Problem Types
        </h1>
        <p className="text-slate-600 font-body">Manage complaint categories</p>
      </div>

      {/* Add New */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold font-headings text-slate-900 mb-4">Add New Problem Type</h2>
        <div className="flex gap-3">
          <input
            type="text"
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
            placeholder="Enter problem type name..."
            className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            data-testid="new-problem-type-input"
          />
          <button
            onClick={handleAdd}
            disabled={adding}
            data-testid="add-problem-type-btn"
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
          >
            {adding ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
            Add
          </button>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-semibold font-headings text-slate-900">
            Existing Problem Types ({problemTypes.length})
          </h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : problemTypes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-slate-500">
            <AlertTriangle size={32} className="mb-2 text-slate-300" />
            <p>No problem types defined yet</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {problemTypes.map((pt) => (
              <li
                key={pt.id}
                className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full" />
                  <span className="font-medium text-slate-900">{pt.name}</span>
                </div>
                <button
                  onClick={() => handleDelete(pt.id, pt.name)}
                  disabled={deleting[pt.id]}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                  title="Delete"
                >
                  {deleting[pt.id] ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Trash2 size={18} />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Info */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-700 font-body">
          <strong>Default Types:</strong> Payment Issue, Tech Issue, PDF Issue, Course Information, Others
          <br />
          Add these types if not already present.
        </p>
      </div>
    </div>
  );
};

export default ProblemTypes;
